import { fetchHTML } from '@utils';
import { pubsub } from '@services';
import { d as debounce } from './debounce.js';

class PredictiveSearchResults extends HTMLElement {
  abortController;

  connectedCallback() {
    this.options = {
      minimumSearchTermLength: 2,
      searchId: this.dataset.searchId,
      hideElement: this.dataset.hideElement,
      resourcesLimit: 10,
      resourcesLimitScope: 'each',
    };

    // Get the search form
    this.search = document.querySelector(`#${this.options.searchId}`);
    if (!this.search) return;

    if (this.options.hideElement) {
      this.elementsToHide = this.getScopedElementById(this.options.hideElement);
    }

    // Handle search input
    this.unsubscribeSearchInput = pubsub.subscribe(
      `search:input:${this.dataset.searchId}`,
      ({ value }) => {
        this.enableLoaders();
        this.updateSearch(value);
      },
    );

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.search.clearSearchInput();
      }
    });

    // Handle search clear from search component
    this.unsubscribeSearchClear = pubsub.subscribe(`search:clear:${this.dataset.searchId}`, () => {
      this.onClearSearchInput();
    });

    // When rendered in a drawer, subscribe to the drawer open-event to clear the input and results
    this.drawerId = this.closest('drawer-element')?.id;
    if (this.drawerId) {
      this.unsubscribeDrawerOpened = pubsub.subscribe(`drawer:opened:${this.drawerId}`, () => {
        // Clear input without focusing (clearSearchInput() calls focus())
        const input = this.search.querySelector('input[type="search"]');
        if (input) input.value = '';
        this.search.toggleClearButton();
        this.clearSearchResults();
      });
    }
  }

  disconnectedCallback() {
    this.unsubscribeSearchInput();
    this.unsubscribeSearchClear();
    if (this.drawerId) {
      this.unsubscribeDrawerOpened();
    }
  }

  /**
   * Clear the search results
   */
  clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Clear the search input
   */
  onClearSearchInput() {
    this.ariaHidden = true;
    this.hideOnSearchResults(false);
  }

  /**
   * Enable loaders
   */
  enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  updateSearch = debounce(searchTerm => this.getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.options.minimumSearchTermLength) {
      this.clearSearchResults();
      this.hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.fetchSearchResults(searchTerm);
  }

  /**
   * Hide other elements when the predictive search results are shown
   */
  hideOnSearchResults(hide = true) {
    if (this.elementsToHide) this.elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetch the search results
   */
  async fetchSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const html = await fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search&resources[limit]=${this.options.resourcesLimit}&resources[limit_scope]=${this.options.resourcesLimitScope}`,
        { signal: this.abortController.signal },
      );
      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = '';
      this.innerHTML = resultsMarkup;
      this.ariaHidden = false;
      this.hideOnSearchResults();
    }
    catch (error) {
      console.error('Error fetching search results:', error);
    }
    finally {
      this.disableLoaders();
    }
  }

  getScopedElementById(elementId) {
    const selector = this.buildIdSelector(elementId);
    const scopes = [this.closest('drawer-element'), this.parentElement, document];

    for (const scope of scopes) {
      if (!scope?.querySelector) continue;
      const element = scope.querySelector(selector);
      if (element) return element;
    }

    return null;
  }

  buildIdSelector(id) {
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    return `#${escapedId}`;
  }

}

customElements.define('predictive-search-results', PredictiveSearchResults);
